
'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { motion } from 'framer-motion';
import { ExternalLink, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from '@/navigation';

export default function ClientsPage() {
  const t = useTranslations('clients');

  const clients = [
    {
      name: "Navruz Textile",
      industry: "E-commerce",
      desc: "To'liq e-commerce sayt — mahsulot katalogi, to'lov tizimi va admin panel",
      tech: ["Next.js", "Stripe", "PostgreSQL"],
      color: "from-[#667eea] to-[#764ba2]",
      url: "#",
      date: "Mart 2026"
    },
    {
      name: "Akademiya Plus",
      industry: "Ta'lim",
      desc: "Online o'quv markazi — kurslar, video darslar va sertifikat tizimi",
      tech: ["React", "Node.js", "MongoDB"],
      color: "from-[#f093fb] to-[#f5576c]",
      url: "#",
      date: "Fevral 2026"
    },
    {
      name: "MedLine Klinika",
      industry: "Tibbiyot",
      desc: "Klinika sayt — shifokorlar, navbat olish va bemorlar kabineti",
      tech: ["Next.js", "Firebase", "Tailwind"],
      color: "from-[#4facfe] to-[#00f2fe]",
      url: "#",
      date: "Yanvar 2026"
    },
    {
      name: "AutoParts UZ",
      industry: "E-commerce",
      desc: "Avtomobil ehtiyot qismlari do'koni — 10 000+ mahsulot, filter tizimi",
      tech: ["Next.js", "Prisma", "PostgreSQL"],
      color: "from-[#43e97b] to-[#38f9d7]",
      url: "#",
      date: "Dekabr 2025"
    },
    {
      name: "GreenFarm Market",
      industry: "E-commerce",
      desc: "Qishloq xo'jaligi mahsulotlari bozori — fermer va xaridor platformasi",
      tech: ["React", "Node.js", "Stripe"],
      color: "from-[#fa709a] to-[#fee140]",
      url: "#",
      date: "Noyabr 2025"
    },
    {
      name: "TourUz Travel",
      industry: "Agentlik",
      desc: "Sayohat agentligi — tur paketlar, bron qilish va to'lov tizimi",
      tech: ["Next.js", "MongoDB", "Tailwind"],
      color: "from-[#a18cd1] to-[#fbc2eb]",
      url: "#",
      date: "Oktyabr 2025"
    }
  ];

  return (
    <main className="min-h-screen bg-white dark:bg-black">
      <Navbar />
      
      {/* HERO */}
      <section className="py-24 md:py-40 border-b border-gray-100 dark:border-zinc-900">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 py-1 border border-gray-200 rounded-full mb-8 dark:border-gray-800"
          >
            <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-black dark:text-white">
              {t('badge')}
            </span>
          </motion.div>
          <h1 className="text-6xl md:text-8xl font-display italic mb-8 dark:text-white">
            {t('title1')} <br /> {t('title2')}
          </h1>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto dark:text-gray-400">
            {t('subtitle')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-3xl mx-auto mt-20">
            {[
              { label: "Loyiha", val: "50+" },
              { label: "Yil", val: "3+" },
              { label: "Mamnuniyat", val: "100%" }
            ].map((stat, i) => (
              <div key={i} className="p-6 rounded-2xl bg-gray-50 dark:bg-zinc-900">
                <div className="text-3xl font-bold dark:text-white">{stat.val}</div>
                <div className="text-xs font-mono uppercase tracking-widest text-gray-400 mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENTS GRID */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {clients.map((client, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-100 rounded-3xl overflow-hidden group hover:border-black transition-all dark:bg-zinc-900 dark:border-zinc-800 dark:hover:border-white"
              >
                {/* Mockup Preview */}
                <div className={`aspect-[16/10] bg-gradient-to-br ${client.color} p-6 relative overflow-hidden`}>
                  <div className="w-full h-full border-4 border-white/20 rounded-xl relative">
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-white/20 rounded-full" />
                    <div className="mt-8 px-4 space-y-4">
                      <div className="w-1/2 h-4 bg-white/20 rounded" />
                      <div className="w-full h-20 bg-white/10 rounded-lg" />
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold dark:text-white">{client.name}</h3>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full dark:bg-indigo-900/30 dark:text-indigo-400">
                        {client.industry}
                      </span>
                    </div>
                    <span className="text-[10px] font-mono text-gray-400">{client.date}</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-6 leading-relaxed dark:text-gray-400">
                    {client.desc}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {client.tech.map(tag => (
                      <span key={tag} className="text-[10px] font-mono px-2 py-1 bg-gray-100 rounded dark:bg-zinc-800 dark:text-gray-400">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <Button asChild variant="outline" className="w-full rounded-xl dark:border-zinc-800">
                    <a href={client.url} onClick={(e) => client.url === '#' && e.preventDefault()}>
                      {t('view_site')}
                    </a>
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-xs italic text-gray-400 mt-12">
            * Mijoz so'roviga ko'ra ba'zi saytlar yashirin holda qoldirilgan. URL tugmasi ishlamasa, sayt NDA ostida.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-black text-white text-center">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-6xl font-display italic mb-8">{t('cta_title')}</h2>
          <Button asChild className="rounded-full h-14 px-10 bg-white text-black hover:bg-gray-200">
            <Link href="/#contact">{t('cta_btn')}</Link>
          </Button>
        </div>
      </section>

      <Footer />
    </main>
  );
}
